import Phaser from 'phaser';

export interface ShimmerOptions {
  scene: Phaser.Scene;
  /** Container that owns the shimmer — sprite + mask are added here, so
   *  the shimmer scrolls / scales with the parent automatically. */
  parent: Phaser.GameObjects.Container;
  /** Bounds of the element to shimmer over, in the parent's coordinate
   *  space. The sweep travels horizontally across these bounds. If
   *  `maskSource` is omitted, this rectangle also doubles as the
   *  GeometryMask clipping the band. */
  bounds: Phaser.Geom.Rectangle;
  /** Optional alpha-shape mask source. When provided, the band is
   *  clipped to the alpha channel of this object's texture instead of
   *  the bounds rect — so the shimmer only brightens the visible pixels
   *  (e.g., the glyphs of a wordmark, not the gaps between letters).
   *  Phaser's BitmapMask samples this object's render output every
   *  frame, so it follows the source through scrolls / scales / etc. */
  maskSource?: Phaser.GameObjects.GameObject;
  /** Full loop period in ms (sweep duration + rest). 4000–6000 reads as
   *  a slow, premium gleam. */
  periodMs?: number;
  /** Sweep-to-rest ratio inside the period. 0.5 ≈ half the period is the
   *  visible sweep, the other half is dark. Lower = brisker sweep with
   *  longer rests. */
  sweepDutyCycle?: number;
  /** Band width as a fraction of the bounds width. ~0.35 reads as a
   *  thin gleam; bump higher for a softer wash. */
  bandRatio?: number;
  /** Peak alpha at the band's center. White at this alpha, ADD-blended
   *  onto the element underneath. */
  peakAlpha?: number;
  /** Skew angle (deg). Tilted bands feel more metallic than vertical
   *  ones; spec calls for a "parallelogram." */
  bandAngleDeg?: number;
  /** Initial delay before the first sweep starts (ms). Useful for
   *  staggering shimmers across multiple elements so they don't all
   *  sweep in lockstep. */
  startDelayMs?: number;
}

/**
 * GPU-cheap shimmer sweep — a translucent white parallelogram sprite
 * tweened across an element on a periodic loop, masked to the element's
 * bounds. Uses BLEND_MODES.ADD so it brightens whatever's underneath
 * rather than overlaying as flat white.
 *
 * Construct once per element (CASINO wordmark, each unlocked tile, etc.)
 * and `.destroy()` on scene shutdown to release the band + mask + tween.
 */
export class Shimmer {
  private band: Phaser.GameObjects.Rectangle;
  /** Hidden Graphics object that defines the GeometryMask shape. Only
   *  populated when no `maskSource` is provided; for BitmapMask shimmers
   *  the source object owns the alpha shape and this stays undefined. */
  private maskShape: Phaser.GameObjects.Graphics | undefined;
  private mask: Phaser.Display.Masks.GeometryMask | Phaser.Display.Masks.BitmapMask;
  private tween: Phaser.Tweens.Tween;
  private destroyed = false;

  constructor(opts: ShimmerOptions) {
    const { scene, parent, bounds, maskSource } = opts;
    const periodMs       = opts.periodMs       ?? 5000;
    const sweepDutyCycle = opts.sweepDutyCycle ?? 0.45;
    const bandRatio      = opts.bandRatio      ?? 0.35;
    const peakAlpha      = opts.peakAlpha      ?? 0.22;
    const bandAngleDeg   = opts.bandAngleDeg   ?? -14;
    const startDelayMs   = opts.startDelayMs   ?? 0;

    const bandW = Math.max(8, Math.round(bounds.width * bandRatio));
    // Band height is the bounds height plus just enough overshoot to cover
    // the corners after rotation — keeping this tight matters when a
    // BitmapMask is in play, since some platforms (notably mobile WebGL)
    // don't perfectly clip a rotated band whose unrotated rect extends far
    // past the alpha shape. The 1.2 multiplier handles rotation up to ~30°.
    const bandH = Math.round(bounds.height * 1.2);

    const startX = bounds.x - bandW;
    const cy = bounds.y + bounds.height / 2;
    this.band = scene.add
      .rectangle(startX, cy, bandW, bandH, 0xffffff, peakAlpha)
      .setOrigin(0, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAngle(bandAngleDeg);
    parent.add(this.band);

    if (maskSource) {
      // BitmapMask samples the source's rendered alpha channel — for
      // Text, that's the glyph outlines themselves, so the band only
      // brightens visible letterforms (no shimmer in the gaps between
      // letters or in the padding around the wordmark).
      this.mask = new Phaser.Display.Masks.BitmapMask(scene, maskSource);
    } else {
      // GeometryMask: hidden Graphics rectangle matching the bounds.
      // The mask travels with the parent container automatically.
      this.maskShape = scene.add.graphics();
      this.maskShape.fillStyle(0xffffff);
      this.maskShape.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
      this.maskShape.setVisible(false);
      parent.add(this.maskShape);
      this.mask = this.maskShape.createGeometryMask();
    }
    this.band.setMask(this.mask);

    // Sweep tween. Sweep happens over (periodMs * sweepDutyCycle); the
    // remainder is rest (band held off-screen to the left, restored via
    // onRepeat). Sine.InOut keeps the gleam silky.
    const sweepMs = Math.max(400, Math.round(periodMs * sweepDutyCycle));
    const restMs  = Math.max(0, periodMs - sweepMs);
    this.tween = scene.tweens.add({
      targets: this.band,
      x: bounds.x + bounds.width + bandW,
      duration: sweepMs,
      delay: startDelayMs,
      repeat: -1,
      repeatDelay: restMs,
      ease: 'Sine.InOut',
      onRepeat: () => { if (!this.destroyed) this.band.setX(startX); },
    });
  }

  /** Toggle the band on / off. The tween keeps running underneath so
   *  the shimmer remains in phase when it's re-shown — callers don't
   *  need to track elapsed time to "resume" the gleam. */
  setVisible(visible: boolean): void {
    if (this.destroyed) return;
    this.band.setVisible(visible);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    // Stop + remove tween first so the sprite isn't repositioned after
    // destroy fires.
    this.tween.stop();
    this.tween.remove();
    this.band.clearMask();
    this.mask.destroy();
    this.maskShape?.destroy();
    this.band.destroy();
  }
}
